import { useEffect, useState } from 'react';
import { API } from '../App';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { BookOpen, Users, Star } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function FeaturedCoursesCard() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [courses, setCourses] = useState([]);

  useEffect(() => {
    fetchCourses();
  }, []);

  const fetchCourses = async () => {
    try {
      const response = await axios.get(`${API}/courses?limit=6`);
      setCourses(response.data.slice(0, 3)); // Show only top 3
    } catch (error) {
      console.error('Error fetching courses:', error);
    }
  };

  if (courses.length === 0) {
    return null; // Don't show if no courses
  }

  return (
    <Card className="p-6 bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 dark:from-blue-900/20 dark:via-purple-900/20 dark:to-pink-900/20 border-2 border-blue-200 dark:border-blue-800">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full">
            <BookOpen className="h-6 w-6 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900 dark:text-white">Featured Courses</h3>
            <p className="text-sm text-blue-600 dark:text-blue-400">
              Learn new skills from expert creators
            </p>
          </div>
        </div>
        <Button
          onClick={() => navigate('/courses')}
          variant="outline"
          size="sm"
          className="border-blue-500 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900"
        >
          View All
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {courses.map((course) => (
          <div
            key={course.id}
            onClick={() => navigate(`/courses/${course.id}`)}
            className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-blue-100 dark:border-blue-700 hover:shadow-lg transition-all cursor-pointer"
          >
            <Badge className="mb-2 bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300">
              {course.category}
            </Badge>
            <h4 className="font-bold text-gray-900 dark:text-white mb-2 line-clamp-1">
              {course.title}
            </h4>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-3 line-clamp-2">
              {course.description}
            </p>
            
            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1 text-gray-600 dark:text-gray-400">
                  <Users className="h-3 w-3" />
                  <span>{course.enrollment_count || 0}</span>
                </div>
                {course.rating_avg > 0 && (
                  <div className="flex items-center gap-1">
                    <Star className="h-3 w-3 text-yellow-400 fill-yellow-400" />
                    <span>{course.rating_avg.toFixed(1)}</span>
                  </div>
                )}
              </div>
              <span className="font-bold text-blue-600 dark:text-blue-400">
                {course.price_gbp === 0 ? 'Free' : `£${course.price_gbp.toFixed(0)}`}
              </span>
            </div>
          </div>
        ))}
      </div>

      <p className="text-xs text-gray-500 dark:text-gray-400 text-center mt-4">
        New courses added by creators daily
      </p>
    </Card>
  );
}
